import React from "react";
import {
  ChevronDownIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import { A } from "../styles/appleClasses";
import { Button } from "./Button";
import { AppleDateInput } from "./AppleDateInput"; // adjust path if needed
import { DateRangeFilter } from "./DateRangeFilter"; // adjust path

export type SortOption = { value: string; label: string };
export type MenuItem = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

function useCloseOnOutsideClick(
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  wrapRef: React.RefObject<HTMLDivElement | null>
) {
  React.useEffect(() => {
    const onDown = (e: MouseEvent | PointerEvent) => {
      const el = wrapRef.current;
      if (!el) return;
      if (open && !el.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open, setOpen, wrapRef]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [setOpen]);
}

function DropdownMenu({
  button,
  items,
  align = "right",
}: {
  button: (args: { open: boolean; toggle: () => void }) => React.ReactNode;
  items: MenuItem[];
  align?: "left" | "right";
}) {
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);
  useCloseOnOutsideClick(open, setOpen, wrapRef);

  const toggle = () => setOpen((v) => !v);

  return (
    <div ref={wrapRef} className="relative inline-block">
      {button({ open, toggle })}

      {open && (
        <div
          role="menu"
          className={[
            "absolute mt-2 rounded-2xl border border-gray-200 bg-white shadow-md p-1 z-50",
            "w-max min-w-[10rem] max-w-[90vw]",
            align === "right" ? "right-0" : "left-0",
          ].join(" ")}
          onClick={() => setOpen(false)}
        >
          {items.map((it, idx) => (
            <button
              key={idx}
              className={`${A.body} w-full text-left px-3 py-2 rounded-xl hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap
                         focus:outline-none focus:ring-2 focus:ring-appleblueD transition`}
              onClick={it.onClick}
              disabled={it.disabled}
              type="button"
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ListToolbar({
  search,
  onSearchChange,

  sortValue,
  sortOptions,
  onSortChange,
  showSort,

  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  showDateRange,

  newItems,
  moreItems,
  showMore,

  rightSummary,
  mobileSummaryWithSearch = false,
  maxWidthClass = "max-w-5xl",
  searchPlaceholder = "Search",
  moreLabel = "More",
  sortLabel = "Sort",
  fromLabel,
  toLabel,
  openCalendarLabel,
}: {
  search: string;
  onSearchChange: (v: string) => void;

  // Optional sort
  sortValue?: string;
  sortOptions?: SortOption[];
  onSortChange?: (v: string) => void;
  showSort?: boolean;

  // Optional date range
  fromDate?: string;
  toDate?: string;
  onFromDateChange?: (v: string) => void;
  onToDateChange?: (v: string) => void;
  showDateRange?: boolean;

  // Buttons
  newItems?: MenuItem[];
  moreItems?: MenuItem[];
  showMore?: boolean;

  // optional right-side summary area (e.g., total)
  rightSummary?: React.ReactNode;
  mobileSummaryWithSearch?: boolean;

  maxWidthClass?: string;
  searchPlaceholder?: string;
  moreLabel?: string;
  sortLabel?: string;
  fromLabel?: string;
  toLabel?: string;
  openCalendarLabel?: string;
}) {
  const _newItems = (newItems || []).filter(Boolean);
  const _moreItems = (moreItems || []).filter(Boolean);

  const showNew = _newItems.length > 0;
  const singleNew = _newItems.length === 1;

  const hasSort =
    typeof sortValue === "string" &&
    Array.isArray(sortOptions) &&
    sortOptions.length > 0 &&
    typeof onSortChange === "function";

  const hasDateRange =
    typeof onFromDateChange === "function" &&
    typeof onToDateChange === "function";

  const showSortActual = showSort ?? hasSort;
  const showDateRangeActual = showDateRange ?? hasDateRange;
  const showMoreActual = showMore ?? _moreItems.length > 0;

  const safeSortValue = sortValue ?? "";
  const safeSortOptions = sortOptions ?? [];
  const safeOnSortChange = onSortChange ?? (() => {});

  const safeFrom = fromDate ?? "";
  const safeTo = toDate ?? "";
  const safeOnFrom = onFromDateChange ?? (() => {});
  const safeOnTo = onToDateChange ?? (() => {});

  const showRow2 = showSortActual || showDateRangeActual || !!rightSummary;

  const brandOutlineBtn =
    "border-appleblue text-appleblue bg-transparent " +
    "hover:bg-appleblue/10 hover:border-appleblue " +
    "active:bg-appleblue/20";

  const NewControl = () => {
    if (!showNew) return null;

    if (singleNew) {
      const it = _newItems[0];
      return (
        <Button
          type="button"
          variant="outline"
          minWidth="auto"
          disabled={it.disabled}
          onClick={it.onClick}
          className="w-full sm:w-auto"
        >
          {it.label}
        </Button>
      );
    }

    return (
      <DropdownMenu
        align="right"
        items={_newItems}
        button={({ toggle }) => (
          <Button
            type="button"
            variant="outline"
            minWidth="auto"
            onClick={toggle}
            className="w-full sm:w-auto"
            rightIcon={<ChevronDownIcon className="w-4 h-4" />}
          >
            + New
          </Button>
        )}
      />
    );
  };

  const MoreControl = () => {
    if (!showMoreActual) return null;

    return (
      <DropdownMenu
        align="right"
        items={_moreItems}
        button={({ toggle }) => (
          <button
            type="button"
            onClick={toggle}
            className={`${A.iconBtn} w-12 inline-flex items-center justify-center border border-gray-200 bg-white hover:bg-gray-50 transition
                       focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue`}
            aria-label={moreLabel}
            aria-haspopup="menu"
          >
            <EllipsisVerticalIcon className="w-5 h-5 text-gray-500" />
          </button>
        )}
      />
    );
  };

  return (
    <div className={`w-full ${maxWidthClass} px-2 mb-4`}>
      {/* ROW 1: Search + optional mobile summary + (New + More) */}
      <div
        className={[
          "grid gap-3 items-center",
          mobileSummaryWithSearch && rightSummary
            ? "grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[1fr_auto]"
            : "grid-cols-1 sm:grid-cols-[1fr_auto]",
        ].join(" ")}
      >
        {/* Search */}
        <div className="relative w-full">
          <span className="absolute left-3 top-3 text-gray-400 pointer-events-none">
            <svg
              width="18"
              height="18"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                d="M20 20l-3-3"
              />
            </svg>
          </span>

          <input
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className={`${A.inputBase} pl-9 pr-4 placeholder-gray-400 appearance-none`}
          />
        </div>

        {mobileSummaryWithSearch && rightSummary ? (
          <div className="sm:hidden flex justify-end items-center">
            {rightSummary}
          </div>
        ) : null}

        {/* Actions: New + More */}
        <div
          className={[
            "w-full sm:w-auto flex gap-2 justify-end",
            mobileSummaryWithSearch && rightSummary ? "col-span-2 sm:col-span-1" : "",
          ].join(" ")}
        >
          {NewControl()}
          {MoreControl()}
        </div>
      </div>

      {/* ROW 2: Sort + From/To + RightSummary */}
      {showRow2 ? (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
          {/* Sort */}
          {showSortActual ? (
            <div className="sm:flex-1 sm:max-w-[18rem]">
              <label className="sr-only" htmlFor="list-sort">
                {sortLabel}
              </label>
              <div className="relative">
                <select
                  id="list-sort"
                  value={safeSortValue}
                  onChange={(e) => safeOnSortChange(e.target.value)}
                  className={`${A.inputBase} pr-10 appearance-none`}
                >
                  {safeSortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>

                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <ChevronDownIcon className="w-4 h-4" />
                </span>
              </div>
            </div>
          ) : null}

          {/* Date range */}
          {showDateRangeActual ? (
            <DateRangeFilter
              variant="range"
              fromLabel={fromLabel}
              toLabel={toLabel}
              openCalendarLabel={openCalendarLabel}
              startDate={safeFrom}
              endDate={safeTo}
              onChange={({ startDate, endDate }) => {
                safeOnFrom(startDate);
                safeOnTo(endDate);
              }}
              className="w-full sm:w-auto"
            />
          ) : null}

          {/* Right summary */}
          {rightSummary ? (
            <div
              className={[
                "sm:ml-auto justify-end items-center",
                mobileSummaryWithSearch ? "hidden sm:flex" : "flex",
              ].join(" ")}
            >
              {rightSummary}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
